'use client';

import { useState } from 'react';

import { Button, Typography } from 'lighthouse';

import { withSeparator } from '@/utilities/withSeparator';

import { useSupportStore } from '@/stores/support/useSupportStore';

import { CAMPAIGN_COUPON_CODE, CAMPAIGN_DISCOUNT_AMOUNT } from '@/constants/batteryToBattery';

import classes from '../../index.module.css';

import { cornbar } from 'cornbar';
import { Copy, Lock1, Mobile, TickCircle, TickSquare } from 'iconsax-react';

interface RewardCardProps {
    isLoggedIn: boolean;
    onLoginRequest: () => void;
    onProductsClick: () => void;
}

const RewardCard = ({ isLoggedIn, onLoginRequest, onProductsClick }: RewardCardProps) => {
    const [isCopied, setIsCopied] = useState(false);
    const openSupport = useSupportStore((store) => store.openSupport);

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(CAMPAIGN_COUPON_CODE);
            setIsCopied(true);
            cornbar.show({
                title: 'کپی شد!',
                description: 'موقع پرداخت سفارش باتری واردش کن.',
                variant: 'success'
            });
            setTimeout(() => setIsCopied(false), 2500);
        } catch {
            cornbar.show({
                title: 'خطا کپی نشد!',
                description: `کد تخفیف: ${CAMPAIGN_COUPON_CODE}`,
                variant: 'error'
            });
        }
    };

    if (!isLoggedIn) {
        return (
            <div key='locked' className={`${classes.popIn} rounded-lg bg-white p-6 text-center shadow-elevation2 ring-1 ring-black/5 sm:p-10`}>
                <span className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10'>
                    <Mobile size={30} color='#008fff' />
                </span>
                <Typography variant='h5' component='h3' fontWeight={800} color='blackHighEmphasis' className='mt-5'>
                    کد تخفیفت فقط یک قدم باهات فاصله داره
                </Typography>
                <Typography variant='body2' component='p' color='blackMediumEmphasis' className='mx-auto mt-3 max-w-md leading-7'>
                    فقط شماره موبایلت رو وارد کن؛ بلافاصله کد تخفیف {CAMPAIGN_DISCOUNT_AMOUNT}٪ خرید باتری بهت نمایش داده می‌شه و می‌تونی باتری به
                    باتری رایگان هم رزرو کنی.
                </Typography>
                <Button size='large' variant='contained' onClick={onLoginRequest} className='mt-6 w-full font-extrabold sm:w-auto sm:min-w-[260px]'>
                    دریافت کد تخفیف و سرویس رایگان
                </Button>
                <Typography variant='caption' component='p' color='blackLowEmphasis' className='mt-4 flex items-center justify-center gap-1.5'>
                    <Lock1 size={14} color='currentcolor' />
                    ورود یا ثبت‌نام با پیامک، در کمتر از یک دقیقه؛ بدون هیچ هزینه‌ای
                </Typography>
            </div>
        );
    }

    return (
        <div key='unlocked' className={`${classes.popIn} rounded-lg bg-gradient-to-l from-amber-300 via-primary to-amber-300 p-[2px]`}>
            <div className='rounded-lg bg-[#071026] p-6 text-center sm:p-10'>
                <span className='relative mx-auto flex h-16 w-16 items-center justify-center'>
                    <span aria-hidden className={`absolute inset-0 rounded-full bg-amber-300 ${classes.successRing}`} />
                    <span className='relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-amber-500'>
                        <TickCircle size={32} color='#071026' />
                    </span>
                </span>

                <Typography variant='h5' component='h3' fontWeight={800} className='mt-5 text-white'>
                    تبریک! کد تخفیفت فعال شد
                </Typography>
                <Typography variant='body2' component='p' className='mx-auto mt-3 max-w-md leading-7 text-white/70'>
                    {withSeparator(CAMPAIGN_DISCOUNT_AMOUNT)} تومان تخفیف خرید باتری و لوازم یدکی؛ این کد رو موقع پرداخت سفارش وارد کن تا قیمتت کمتر
                    بشه.
                </Typography>
                <Typography variant='body2' component='p' className='mx-auto mt-3 max-w-md leading-7 text-white/70'>
                    برای دریافت باتری به باتری رایگان در محل، کافیه با پشتیبانی ازکی‌سرویس تماس بگیری تا کارشناس اعزام بشه.
                </Typography>
                <div
                    dir='ltr'
                    className='mx-auto mt-6 flex max-w-md items-center justify-between gap-3 rounded-lg border-2 border-dashed border-amber-300/60
                        bg-white/5 px-4 py-3'>
                    <span className='font-mono select-all text-lg font-bold tracking-[0.25em] text-amber-300 sm:text-xl'>{CAMPAIGN_COUPON_CODE}</span>
                    <Button
                        size='small'
                        variant={isCopied ? 'contained' : 'outlined'}
                        onClick={handleCopyCode}
                        startIcon={
                            isCopied ? (
                                <TickSquare size={16} color='currentcolor' className='mr-2' />
                            ) : (
                                <Copy size={16} color='currentcolor' className='mr-2' />
                            )
                        }
                        className={isCopied ? 'bg-success text-white' : 'border-white/30 text-white hover:border-white/60'}>
                        {isCopied ? 'کپی شد' : 'کپی کد'}
                    </Button>
                </div>

                <div className='flex justify-center gap-x-3'>
                    <Button
                        size='large'
                        variant='contained'
                        onClick={onProductsClick}
                        className={`mt-6 w-full bg-amber-400 font-extrabold text-[#0A1530] hover:bg-amber-300 sm:w-auto ${classes.glowPulse}`}>
                        خرید با {withSeparator(CAMPAIGN_DISCOUNT_AMOUNT)} تومان تخفیف
                    </Button>
                    <Button
                        size='large'
                        variant='outlined'
                        onClick={() => openSupport('campaign', 'battery')}
                        className={'mt-6 w-full font-extrabold text-white sm:w-auto'}>
                        راهنمایی برای باتری به باتری رایگان
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RewardCard;
